import { IntegrationTypeEnum, ProviderIntegrationTypeEnum } from 'modules/global/typing/enums/integrations.enum';

export const SMS_PROVIDER = {
  TWILIO: 'Twilio',
  TYNTEC: 'Tyntec',
  CMCOM: 'CmCom'
} as const;

export type SMS_PROVIDER = (typeof SMS_PROVIDER)[keyof typeof SMS_PROVIDER];

/** Integrations mapping */
export const INTEGRATIONS_DATA_NAME = {
  [IntegrationTypeEnum.SERVICE_APP]: 'serviceApp',
  [IntegrationTypeEnum.BMW_WSV]: 'wsv',
  [IntegrationTypeEnum.WYZ]: 'wyz',
  [IntegrationTypeEnum.BEE2LINK]: 'bee2Link',
  [IntegrationTypeEnum.BODY_SCAN]: 'bodyScan',
  [IntegrationTypeEnum.EASY_TREAD]: 'easyTread',
  [IntegrationTypeEnum.DMS]: 'dms',
  [IntegrationTypeEnum.ISPA]: 'ispa',
  [IntegrationTypeEnum.KEYLOOP]: 'keyloop',
  [IntegrationTypeEnum.MECA_PLANNING]: 'mecaPlanning',
  [IntegrationTypeEnum.MY_BMW_APP]: 'myBMWApp',
  [IntegrationTypeEnum.NEXTLANE]: 'nextlane',
  [IntegrationTypeEnum.OPEN_FLEX]: 'openFlex',
  [IntegrationTypeEnum.PISA]: 'pisa',
  [IntegrationTypeEnum.PLANET_VO]: 'planetVo',
  [IntegrationTypeEnum.PROOVSTATION]: 'proovStation',
  [IntegrationTypeEnum.SOFT_NRG]: 'softNrg',
  [IntegrationTypeEnum.SOFT_PLANNING]: 'softPlanning',
  [IntegrationTypeEnum.SOFT_VIG]: 'softVig',
  [IntegrationTypeEnum.TCHEK]: 'tchek',
  [IntegrationTypeEnum.TIRE_QUOTATION]: 'tireQuotation',
  [IntegrationTypeEnum.TIRE_SCAN]: 'tireScan',
  [IntegrationTypeEnum.TSCAN]: 'tscan',
  [IntegrationTypeEnum.XENTRY]: 'xentry',
  [IntegrationTypeEnum.XTIME]: 'xtime',
  [IntegrationTypeEnum.TACDIS]: 'tacdis',
  [IntegrationTypeEnum.XPOWER]: 'xpower'
} as Record<IntegrationTypeEnum, string>;

export const FLEETBACK_DMS_DATA_NAME = 'fleetbackDms';

export const DMS_PROVIDER_INTEGRATIONS_DATA_NAME = [
  FLEETBACK_DMS_DATA_NAME,
  INTEGRATIONS_DATA_NAME[IntegrationTypeEnum.MECA_PLANNING],
  INTEGRATIONS_DATA_NAME[IntegrationTypeEnum.KEYLOOP],
  INTEGRATIONS_DATA_NAME[IntegrationTypeEnum.NEXTLANE],
];

export const BMW_INTEGRATIONS = [
  IntegrationTypeEnum.SERVICE_APP,
  IntegrationTypeEnum.MY_BMW_APP,
  IntegrationTypeEnum.PISA,
  IntegrationTypeEnum.BMW_WSV
];

export const BODY_SCAN_INTEGRATIONS = [IntegrationTypeEnum.TCHEK, IntegrationTypeEnum.BODY_SCAN];

export const TIRE_QUOTATION_INTEGRATIONS = [
  IntegrationTypeEnum.TIRE_QUOTATION,
];

/**
 * Represents the integrations that support tire quote import.
 *
 * This list includes:
 * - Integrations dedicated to tire quotation (`TIRE_QUOTATION_INTEGRATIONS`)
 * - Additional integrations that can also provide tire quotes.
 *
 * Note:
 * - `IntegrationTypeEnum.WYZ` is primarily a tire scan integration,
 *   but it can also provide tire quotes, therefore, it is included here.
 */
export const TIRE_QUOTE_IMPORT_INTEGRATIONS = [
  ...TIRE_QUOTATION_INTEGRATIONS,
  IntegrationTypeEnum.WYZ
];

export const TIRE_SCAN_INTEGRATIONS = [
  IntegrationTypeEnum.PROOVSTATION,
  IntegrationTypeEnum.EASY_TREAD,
  IntegrationTypeEnum.TSCAN,
  IntegrationTypeEnum.TIRE_SCAN,
  IntegrationTypeEnum.ANYLINE,
  IntegrationTypeEnum.WYZ
];

export const PLATE_RECOGNIZER_INTEGRATIONS = [IntegrationTypeEnum.PLATE_RECOGNIZER, IntegrationTypeEnum.OPEN_ALPR];

export const DMS_INTEGRATIONS = [
  IntegrationTypeEnum.SOFT_NRG,
  IntegrationTypeEnum.ISPA,
  IntegrationTypeEnum.DMS,
  IntegrationTypeEnum.KEYLOOP,
  IntegrationTypeEnum.MECA_PLANNING,
  IntegrationTypeEnum.NEXTLANE,
  IntegrationTypeEnum.SOFT_VIG,
  IntegrationTypeEnum.TACDIS,
  IntegrationTypeEnum.XTIME,
  IntegrationTypeEnum.XPOWER,
  IntegrationTypeEnum.SOFT_PLANNING
];

/**
 * Integrations that force `config.integration.enablePreviouslyProposedWorks` to be enabled and read-only:
 * they always import previously proposed works, so the dealer cannot opt out.
 */
export const PREVIOUSLY_PROPOSED_WORKS_INTEGRATIONS: IntegrationTypeEnum[] = [
  IntegrationTypeEnum.ISPA,
  IntegrationTypeEnum.SOFT_PLANNING
];

export const STEPS_INTEGRATIONS = [
  IntegrationTypeEnum.VELOSY,
  IntegrationTypeEnum.DIGGI_DISPO,
];

export const SALES_DISTRIBUTION_INTEGRATIONS = [
  IntegrationTypeEnum.BEE2LINK,
  IntegrationTypeEnum.OPEN_FLEX,
  IntegrationTypeEnum.PLANET_VO
];

export const DATA_HUB_INTEGRATIONS = [IntegrationTypeEnum.XENTRY];

export const MOBILE_LINKED_DEVICES_INTEGRATIONS = [
  IntegrationTypeEnum.INSPECTRON
];

/**
 * Integrations exposed in the Dealership Configuration integrations list.
 *
 * Allow-list on purpose: since `IntegrationTypeEnum` is spread from `@arhs-fleetback/types`, a
 * future package bump adding a member would otherwise surface it in the UI silently. Anything
 * not listed here defaults to hidden, so a new member stays hidden until deliberately added.
 */
const VISIBLE_INTEGRATIONS: IntegrationTypeEnum[] = [
  IntegrationTypeEnum.AUTOMATED_DOCUMENT_VALIDATION,
  IntegrationTypeEnum.BEE2LINK,
  IntegrationTypeEnum.BMW_WSV,
  IntegrationTypeEnum.BODY_SCAN,
  IntegrationTypeEnum.DMS,
  IntegrationTypeEnum.EASY_TREAD,
  IntegrationTypeEnum.ISPA,
  IntegrationTypeEnum.KEYLOOP,
  IntegrationTypeEnum.MECA_PLANNING,
  IntegrationTypeEnum.MY_BMW_APP,
  IntegrationTypeEnum.NEXTLANE,
  IntegrationTypeEnum.OPEN_FLEX,
  IntegrationTypeEnum.PISA,
  IntegrationTypeEnum.PLANET_VO,
  IntegrationTypeEnum.PROOVSTATION,
  IntegrationTypeEnum.SERVICE_APP,
  IntegrationTypeEnum.SOFT_NRG,
  IntegrationTypeEnum.SOFT_PLANNING,
  IntegrationTypeEnum.SOFT_VIG,
  IntegrationTypeEnum.TACDIS,
  IntegrationTypeEnum.TCHEK,
  IntegrationTypeEnum.TIRE_QUOTATION,
  IntegrationTypeEnum.TIRE_SCAN,
  IntegrationTypeEnum.TSCAN,
  IntegrationTypeEnum.WYZ,
  IntegrationTypeEnum.XENTRY,
  IntegrationTypeEnum.XPOWER,
  IntegrationTypeEnum.XTIME
];

export const INTEGRATIONS = Object.values(IntegrationTypeEnum).filter(
  integration => VISIBLE_INTEGRATIONS.includes(integration)
);

// SMS Providers mapping
export const SMS_PROVIDERS = [SMS_PROVIDER.TWILIO, SMS_PROVIDER.TYNTEC, SMS_PROVIDER.CMCOM];

export const SMS_PROVIDERS_DISPLAY_NAME: Partial<Record<SMS_PROVIDER, string>> = {
  [SMS_PROVIDER.CMCOM]: 'CM.com'
};

export const SMS_PROVIDERS_DISPLAY_NAME_UPPERCASE = Object.entries(SMS_PROVIDERS_DISPLAY_NAME).reduce<Record<string, string | undefined>>(
  (acc, [key, value]) => ({
    ...acc,
    [key.toUpperCase()]: value
  }),
  {}
);

export const INFO_MEDIARIES_INTEGRATIONS = [
  IntegrationTypeEnum.PLANET_VO,
  IntegrationTypeEnum.BEE2LINK,
  IntegrationTypeEnum.OPEN_FLEX
];

export const PROVIDERS_BODY_SCAN_INTEGRATION = [ProviderIntegrationTypeEnum.TWO_TRONIC,
  ProviderIntegrationTypeEnum.EFLEETCON,
  ProviderIntegrationTypeEnum.INSTAVALO];

export const INTEGRATION_DISPLAY_NAME = {
  [IntegrationTypeEnum.PROOVSTATION]: 'ProovStation',
  [IntegrationTypeEnum.BEZAHL_DE]: 'Bezahl.de',
  [IntegrationTypeEnum.OPEN_FLEX]: 'Open Flex'
};

export const PROVIDER_INTEGRATION_TYPE_DISPLAY_NAME = {
  [ProviderIntegrationTypeEnum.CA_ROOL]: 'CaRool',
  [ProviderIntegrationTypeEnum.TWO_TRONIC]: 'Two Tronic',
  [ProviderIntegrationTypeEnum.INSTAVALO]: 'Instavalo',
  [ProviderIntegrationTypeEnum.EFLEETCON]: 'efleetcon',
};

export const PROCESS_TYPE = {
  LIVE: 'LIVE',
  DELAYED: 'DELAYED'
};

export const PAYER_INTEGRATIONS = [
  IntegrationTypeEnum.ISPA,
  IntegrationTypeEnum.KEYLOOP,
  IntegrationTypeEnum.MECA_PLANNING,
  IntegrationTypeEnum.NEXTLANE,
  IntegrationTypeEnum.SOFT_NRG,
  IntegrationTypeEnum.DMS,
  IntegrationTypeEnum.XTIME
];

export const DMS_PROVIDER_NAME = {
  AUTOMASTER: 'Automaster',
  KOBRA: 'Kobra II',
};
